import axios from 'axios';

// axios.defaults.baseURL ya termina en /api/v1 (ver plugins/vue-axios.js),
// así que las rutas van relativas a eso, como en el resto de src/api.
const BASE = '/sueldos';

// Con qué frecuencia se paga un período. Es una etiqueta y sugiere las fechas:
// el importe lo deciden los días del período, no la periodicidad.
// Espeja CondicionLaboral.PERIODICIDADES_PAGO en el backend.
export const PERIODICIDADES_PAGO = [
  { valor: 'SEMANAL', label: 'Semanal' },
  { valor: 'QUINCENAL', label: 'Quincenal' },
  { valor: 'MENSUAL', label: 'Mensual' },
  { valor: 'PERSONALIZADO', label: 'Personalizado (por días)' },
];

// De dónde sale el importe del sueldo fijo. Espeja Liquidacion.BASES_CALCULO.
export const BASES_CALCULO = [
  { valor: 'PRORRATEO', label: 'Pago a cuenta (proporcional a los días)' },
  { valor: 'SALDO_MES', label: 'Cierre de mes (lo que falta para completarlo)' },
];

export default {
  // Condiciones Laborales
  condiciones: {
    index({ empleado = null, vigentes = false } = {}) {
      const params = new URLSearchParams();
      if (empleado) params.append('empleado', empleado);
      if (vigentes) params.append('vigentes', 'true');
      const query = params.toString();
      return axios.get(query ? `${BASE}/condiciones?${query}` : `${BASE}/condiciones`);
    },

    show(id) {
      return axios.get(`${BASE}/condiciones/${id}`);
    },

    create(data) {
      return axios.post(`${BASE}/condiciones`, data);
    },

    update(id, data) {
      return axios.put(`${BASE}/condiciones/${id}`, data);
    },

    destroy(id) {
      return axios.delete(`${BASE}/condiciones/${id}`);
    },
  },

  // Registro de Horas
  horas: {
    index({ empleado = null, desde = null, hasta = null, sin_liquidar = false } = {}) {
      const params = new URLSearchParams();
      if (empleado) params.append('empleado', empleado);
      if (desde) params.append('desde', desde);
      if (hasta) params.append('hasta', hasta);
      if (sin_liquidar) params.append('sin_liquidar', 'true');
      const query = params.toString();
      return axios.get(query ? `${BASE}/horas?${query}` : `${BASE}/horas`);
    },

    show(id) {
      return axios.get(`${BASE}/horas/${id}`);
    },

    create(data) {
      return axios.post(`${BASE}/horas`, data);
    },

    update(id, data) {
      return axios.put(`${BASE}/horas/${id}`, data);
    },

    destroy(id) {
      return axios.delete(`${BASE}/horas/${id}`);
    },
  },

  // Liquidaciones
  liquidaciones: {
    index({ empleado = null, estado = null, desde = null, hasta = null } = {}) {
      const params = new URLSearchParams();
      if (empleado) params.append('empleado', empleado);
      if (estado) params.append('estado', estado);
      if (desde) params.append('desde', desde);
      if (hasta) params.append('hasta', hasta);
      const query = params.toString();
      return axios.get(query ? `${BASE}/liquidaciones?${query}` : `${BASE}/liquidaciones`);
    },

    show(id) {
      return axios.get(`${BASE}/liquidaciones/${id}`);
    },

    create(data) {
      return axios.post(`${BASE}/liquidaciones`, data);
    },

    // Solo período, periodicidad y observaciones, y solo en borrador: el resto
    // lo regenera el recálculo del backend.
    update(id, data) {
      return axios.patch(`${BASE}/liquidaciones/${id}`, data);
    },

    // Con qué periodicidad cobra el empleado, qué período le toca y cuánto daría.
    // Es lo que llena el modal de alta antes de crear nada.
    preparar({
      empleado, periodicidad = null, baseCalculo = null,
      fecha = null, desde = null, hasta = null,
    }) {
      const params = new URLSearchParams({ empleado });
      if (periodicidad) params.append('periodicidad', periodicidad);
      if (baseCalculo) params.append('base_calculo', baseCalculo);
      if (fecha) params.append('fecha', fecha);
      if (desde && hasta) {
        params.append('desde', desde);
        params.append('hasta', hasta);
      }
      return axios.get(`${BASE}/liquidaciones/preparar?${params.toString()}`);
    },

    recalcular(id) {
      return axios.post(`${BASE}/liquidaciones/${id}/recalcular`);
    },

    agregarConcepto(id, data) {
      return axios.post(`${BASE}/liquidaciones/${id}/conceptos`, data);
    },

    eliminarConcepto(id, conceptoId) {
      return axios.delete(`${BASE}/liquidaciones/${id}/conceptos/${conceptoId}`);
    },

    confirmar(id, data) {
      return axios.post(`${BASE}/liquidaciones/${id}/confirmar`, data);
    },

    anular(id) {
      return axios.post(`${BASE}/liquidaciones/${id}/anular`);
    },

    pdf(id) {
      // Sin responseType blob axios lo parsea como texto y el archivo sale corrupto
      return axios.get(`${BASE}/liquidaciones/${id}/pdf`, { responseType: 'blob' });
    },
  },

  // Listas que llenan los combos. No son de /sueldos: viven en empleados y egresos.
  opciones: {
    // Ojo: este endpoint lleva barra final y devuelve { empleados: [...] },
    // no un array pelado. Trae también los inactivos.
    empleados() {
      return axios.get('/empleados/empresa/')
        .then(({ data }) => (data.empleados || []).filter(e => e.is_active));
    },

    // El backend compara esIngreso contra el string 'True', así que cualquier
    // otro valor devuelve egresos. Ya filtra por sucursal y por mostrar=True.
    tiposEgreso() {
      return axios.get('/egresos/tipoingresoegreso', { params: { esIngreso: 'False' } })
        .then(({ data }) => data);
    },

    // Es un árbol: la raíz (parent null) no es seleccionable
    categorias() {
      return axios.get('/egresos/categorias')
        .then(({ data }) => data.filter(c => c.parent !== null));
    },
  },
};
